
import { supabase } from "@/integrations/supabase/client";
import { ServiceContactSubmission } from "@/components/services/ServiceContactSchema";
import { sanitizeInput } from "@/utils/sanitization";

// Get the Supabase URL and anon key from the environment variables
// These are available in the client.ts file as constants
const SUPABASE_URL = "https://yeydokmtpjglqmpgragc.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InlleWRva210cGpnbHFtcGdyYWdjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Mzc1MTQzODUsImV4cCI6MjA1MzA5MDM4NX0.kwHOE8viTuhEJ5d47sSKyEs4BdCySd73kERVwMhGqrk";

export const submitServiceContact = async (data: ServiceContactSubmission) => {
  try {
    console.log("Submitting service contact request:", data);

    // Sanitize inputs to prevent XSS
    const sanitizedData = {
      name: sanitizeInput(data.name),
      email: sanitizeInput(data.email),
      phone: sanitizeInput(data.phone),
      message: data.message ? sanitizeInput(data.message) : "",
      serviceName: data.serviceName ? sanitizeInput(data.serviceName) : "General Service",
      pageUrl: sanitizeInput(data.pageUrl),
    };

    // Use sessionStorage instead of localStorage to track submissions per browser session
    // and reduce the time window from 5 minutes to 30 seconds for testing
    const lastSubmitTime = sessionStorage.getItem('lastContactSubmitTime');
    const now = Date.now();
    
    // Only apply rate limiting if a submission was made in the last 30 seconds
    if (lastSubmitTime && (now - parseInt(lastSubmitTime)) < 30 * 1000) { 
      console.log("Rate limiting applied, last submit was", (now - parseInt(lastSubmitTime))/1000, "seconds ago");
      throw new Error("Too many requests. Please try again in 30 seconds.");
    }
    
    // Set last submit time to session storage
    sessionStorage.setItem('lastContactSubmitTime', now.toString());

    // Log the request data for debugging
    console.log("Prepared data for Edge function:", {
      name: sanitizedData.name,
      email: sanitizedData.email,
      phone: sanitizedData.phone,
      message: sanitizedData.message,
      service_type: sanitizedData.serviceName,
      page_url: sanitizedData.pageUrl,
      emailSubject: sanitizedData.serviceName.toLowerCase().includes("quote request") 
          ? `New Quote Request from ${sanitizedData.serviceName.replace("Quote Request: ", "")}`
          : `New Service Lead: ${sanitizedData.serviceName}`
    });

    // Using the fetch API directly to call the Edge Function
    // Instead of accessing protected properties, use the constants
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/handle-service-contact`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`
        },
        body: JSON.stringify({
          name: sanitizedData.name,
          email: sanitizedData.email,
          phone: sanitizedData.phone,
          message: sanitizedData.message,
          service_type: sanitizedData.serviceName,
          page_url: sanitizedData.pageUrl,
          emailSubject: sanitizedData.serviceName.toLowerCase().includes("quote request") 
            ? `New Quote Request from ${sanitizedData.serviceName.replace("Quote Request: ", "")}`
            : `New Service Lead: ${sanitizedData.serviceName}`
        })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      console.error("Error response from handle-service-contact function:", errorData || response.statusText);
      throw new Error(`Function error: ${errorData?.error || response.statusText}`);
    }
    
    const responseData = await response.json();
    console.log("Service contact function response:", responseData);
    return true;
  } catch (error: any) {
    console.error("Service contact submission error:", error);
    throw error;
  }
};
